import { rootApi } from '@components/rootApi';
import { convertParamsToSnakeCase } from '@utils/api';
import {
  Campaign,
  GetCampaignParams,
  GetCampaignsParams,
  campaignSchema,
  campaignsResponseSchema,
  CampaignsResponseSchema,
  UpdateCampaignParams,
} from '@features/campaigns';

import {
  RslReport,
  RslReportsResponse,
  rslReportsResponseSchema,
} from './schema';

export const RSL_REPORT_TAG = 'RslReport' as const;

export type RslReportsParams = {
  campaignId: number;
  includeUnapproved: boolean;
  showRestrictedValues: boolean;
  channel: string | null;
};

const rslApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getRslReports: builder.query<RslReportsResponse, RslReportsParams>({
      query: params => ({
        url: `/api/v3/rsl/campaigns/${params.campaignId}/reports`,
        params: convertParamsToSnakeCase({
          ...params,
          channel: params.channel ?? undefined,
        }),
      }),
      transformResponse: (response: RslReportsResponse) =>
        rslReportsResponseSchema.parse(response),
      providesTags: result =>
        result?.data.reports.map(({ id }) => ({ type: RSL_REPORT_TAG, id })) ??
        [],
    }),

    updateRslReport: builder.mutation<
      void,
      {
        report: Pick<RslReport, 'id' | 'campaignId' | 'status'>;
        campaignsParams: GetCampaignsParams;
        reportsParams: RslReportsParams;
        isLatestReport: boolean;
        channel: string | null;
      }
    >({
      query: ({ report: { id, campaignId, ...body } }) => ({
        url: `/api/v3/rsl/campaigns/${campaignId}/reports/${id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const { campaignsParams, report, reportsParams } = params;

        const getReportsResult = dispatch(
          rslApi.util.updateQueryData('getRslReports', reportsParams, draft => {
            const reportIndex = draft.data.reports.findIndex(
              r => r.id === report.id
            );
            const draftReport = draft.data.reports[reportIndex];
            if (!draftReport) {
              console.warn('Report not found in cache');
              return;
            }

            draftReport.status = report.status;
          })
        );

        const getCampaignsResult = [
          dispatch(
            rslApi.util.updateQueryData(
              'getRslCampaigns',
              campaignsParams,
              draft => {
                if (!params.isLatestReport || params.channel !== null) return;

                const campaign = draft.campaigns.find(
                  c => c.id === report.campaignId
                );
                if (!campaign) return;

                campaign.latestReportStatus = report.status;
              }
            )
          ),
        ];

        const results = [getReportsResult, ...getCampaignsResult];

        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          results.forEach(result => result.undo());
        }
      },
    }),

    getRslCampaign: builder.query<Campaign, GetCampaignParams>({
      query: ({ id, ...params }) => ({
        url: `/api/v3/rsl/campaigns/${id}`,
        params,
      }),
      transformResponse: (response: Campaign) => campaignSchema.parse(response),
    }),

    getRslCampaigns: builder.query<CampaignsResponseSchema, GetCampaignsParams>(
      {
        query: params => ({
          url: '/api/v3/rsl/campaigns',
          params,
        }),
        transformResponse: (response: CampaignsResponseSchema) =>
          campaignsResponseSchema.parse(response),
      }
    ),

    getRslReportDates: builder.query<string[], void>({
      query: () => ({
        url: '/api/v3/rsl/campaigns/report_dates',
      }),
    }),

    updateRslCampaign: builder.mutation<Campaign, UpdateCampaignParams>({
      query: ({ id, getCampaignsParams: _sideEffectParams, ...body }) => ({
        url: `/api/v3/rsl/campaigns/${id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const result = dispatch(
          rslApi.util.updateQueryData(
            'getRslCampaigns',
            params.getCampaignsParams,
            draft => {
              const campaignIndex = draft.campaigns.findIndex(
                c => c.id === params.id
              );
              const campaign = draft.campaigns[campaignIndex];
              if (!campaign) {
                console.warn('Campaign not found in cache');
                return;
              }

              if (params.archived !== undefined) {
                campaign.archived = params.archived;
              }
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (e) {
          console.error(e);
          result.undo();
        }
      },
    }),

    bulkUpdateRslReports: builder.mutation<
      void,
      {
        campaignId: number;
        reportDate: string;
        status: 'approved' | 'pending';
      }
    >({
      query: ({ campaignId, ...body }) => ({
        url: `/api/v3/rsl/campaigns/${campaignId}/reports/bulk_update`,
        method: 'POST',
        body: convertParamsToSnakeCase(body),
      }),
      invalidatesTags: [{ type: RSL_REPORT_TAG }],
    }),
  }),
});

export const {
  useGetRslReportsQuery,
  useUpdateRslReportMutation,
  useGetRslCampaignQuery,
  useGetRslCampaignsQuery,
  useUpdateRslCampaignMutation,
  useGetRslReportDatesQuery,
  useBulkUpdateRslReportsMutation,
} = rslApi;
